import { BizouLogo } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";

export default function Privacy() {
  const { t, lang } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-[#F0E8EB]">
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between h-14">
          <a href="/" className="flex items-center"><BizouLogo size="md" /></a>
          <LanguageSelector />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-medium text-[#1C1917] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t("privacy.title")}
        </h1>
        <p className="text-sm text-[#78716C] mb-8">{t("privacy.lastUpdate")}</p>

        {lang === "fr" ? <PrivacyContentFR /> : <PrivacyContentEN />}
      </main>
    </div>
  );
}

function PrivacyContentFR() {
  return (
    <div className="prose prose-lg max-w-none text-[#1C1917] space-y-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <section>
        <h2 className="text-xl font-semibold mb-3">1. Responsable de traitement</h2>
        <p className="text-[#78716C] leading-relaxed">
          Le responsable du traitement des données personnelles est la société <strong>My-Memoire SASU</strong>, éditrice du service BIZOU.me.<br />
          Contact : <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a>
        </p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">2. Données collectées</h2>
        <p className="text-[#78716C] leading-relaxed mb-3">Dans le cadre de l'utilisation du service BIZOU.me, nous collectons :</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Données d'identification</strong> : nom, prénom, adresse email (via Google OAuth)</li>
          <li><strong>Contenu des messages</strong> : texte, photos, vidéos que vous choisissez d'enregistrer</li>
          <li><strong>Données du destinataire</strong> : prénom et adresse email du destinataire désigné</li>
          <li><strong>Données de paiement</strong> : traitées exclusivement par Stripe, nous ne stockons aucune donnée bancaire</li>
          <li><strong>Données techniques</strong> : adresse IP, date et heure de connexion, données de navigation</li>
          <li><strong>Preuve de consentement</strong> : horodatage et adresse IP lors de l'acceptation des conditions</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">3. Finalités du traitement</h2>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Exécution du service</strong> : conservation et transmission différée de votre message au destinataire à la date programmée</li>
          <li><strong>Gestion du compte</strong> : authentification, gestion de vos messages et de votre espace personnel</li>
          <li><strong>Paiement</strong> : traitement sécurisé de votre paiement via Stripe</li>
          <li><strong>Amélioration du service</strong> : statistiques d'utilisation anonymisées (avec votre consentement)</li>
          <li><strong>Obligations légales</strong> : conservation des données de transaction conformément à la loi</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">4. Bases légales</h2>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Exécution du contrat</strong> (art. 6.1.b RGPD) : pour la fourniture du service de messagerie différée</li>
          <li><strong>Consentement</strong> (art. 6.1.a RGPD) : pour les cookies analytiques et le dépôt de traceurs</li>
          <li><strong>Intérêt légitime</strong> (art. 6.1.f RGPD) : pour le traitement des données du destinataire, strictement limité à la remise du message</li>
          <li><strong>Obligation légale</strong> (art. 6.1.c RGPD) : pour la conservation des données de facturation</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">5. Données du destinataire</h2>
        <p className="text-[#78716C] leading-relaxed">
          Les informations du destinataire (prénom et adresse email) sont collectées uniquement pour la remise du message programmé.
          Elles ne sont <strong>jamais utilisées à des fins de prospection commerciale</strong> ni transmises à des tiers.
          Le destinataire est informé du traitement de ses données lors de la réception du message et peut exercer ses droits
          en contactant <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a>.
        </p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">6. Durée de conservation</h2>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Messages et fichiers</strong> : conservés jusqu'à la date de livraison, puis supprimés automatiquement après 3 ans</li>
          <li><strong>Données de compte</strong> : conservées pendant la durée d'utilisation du service, puis 3 ans après la dernière activité</li>
          <li><strong>Données de paiement</strong> : conservées 10 ans conformément aux obligations comptables</li>
          <li><strong>Logs techniques</strong> : conservés 1 an maximum</li>
          <li><strong>Preuves de consentement</strong> : conservées 5 ans</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">7. Vos droits</h2>
        <p className="text-[#78716C] leading-relaxed mb-3">Conformément au RGPD, vous disposez des droits suivants :</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Droit d'accès</strong> : obtenir une copie de vos données personnelles</li>
          <li><strong>Droit de rectification</strong> : corriger vos données inexactes</li>
          <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données et de votre compte</li>
          <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
          <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
          <li><strong>Droit à la limitation</strong> : restreindre le traitement de vos données</li>
        </ul>
        <p className="text-[#78716C] leading-relaxed mt-3">
          Pour exercer vos droits, contactez-nous à <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a>.
          Vous pouvez également télécharger ou supprimer vos données directement depuis votre espace personnel.
        </p>
        <p className="text-[#78716C] leading-relaxed mt-3">
          En cas de réclamation, vous pouvez saisir la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#E8175D] hover:underline">www.cnil.fr</a>.
        </p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">8. Hébergement et sécurité</h2>
        <p className="text-[#78716C] leading-relaxed">Vos données sont hébergées sur des serveurs sécurisés situés en Union Européenne. Nous mettons en œuvre les mesures techniques et organisationnelles appropriées pour protéger vos données :</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2 mt-3">
          <li>Chiffrement des communications (HTTPS/TLS)</li>
          <li>Authentification sécurisée via Google OAuth 2.0</li>
          <li>Accès aux fichiers restreint par vérification d'identité</li>
          <li>Paiements sécurisés via Stripe (certifié PCI-DSS)</li>
          <li>Sauvegardes régulières</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">9. Cookies</h2>
        <p className="text-[#78716C] leading-relaxed mb-3">BIZOU.me utilise les cookies suivants :</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Cookies essentiels</strong> (session d'authentification) : nécessaires au fonctionnement du service, exemptés de consentement</li>
          <li><strong>Cookies analytiques</strong> (Google Analytics, Microsoft Clarity) : déposés uniquement avec votre consentement explicite, pour mesurer l'audience du site et analyser le parcours utilisateur (heatmaps, enregistrements de session anonymisés)</li>
          <li><strong>Cookies marketing</strong> (Meta Pixel) : déposés uniquement avec votre consentement explicite</li>
        </ul>
        <p className="text-[#78716C] leading-relaxed mt-3">Vous pouvez modifier vos préférences à tout moment en cliquant sur "Gérer les cookies" en bas de page.</p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">10. Modifications</h2>
        <p className="text-[#78716C] leading-relaxed">
          Nous nous réservons le droit de modifier cette politique de confidentialité. En cas de modification substantielle,
          vous serez informé(e) par email ou via une notification sur le service. La date de dernière mise à jour est indiquée en haut de cette page.
        </p>
      </section>
      <section className="bg-[#FDF0F4] rounded-2xl p-6 mt-10">
        <h2 className="text-xl font-semibold mb-3">Contact</h2>
        <p className="text-[#78716C] leading-relaxed">
          Pour toute question relative à la protection de vos données :<br />
          <strong>My-Memoire SASU</strong><br />
          Email : <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a><br />
          Site : <a href="http://www.my-memoire.com" target="_blank" rel="noopener noreferrer" className="text-[#E8175D] hover:underline">www.my-memoire.com</a>
        </p>
      </section>
    </div>
  );
}

function PrivacyContentEN() {
  return (
    <div className="prose prose-lg max-w-none text-[#1C1917] space-y-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <section>
        <h2 className="text-xl font-semibold mb-3">1. Data Controller</h2>
        <p className="text-[#78716C] leading-relaxed">
          The data controller is <strong>My-Memoire SASU</strong>, publisher of the BIZOU.me service.<br />
          Contact: <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a>
        </p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">2. Data Collected</h2>
        <p className="text-[#78716C] leading-relaxed mb-3">When using the BIZOU.me service, we collect:</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Identification data</strong>: name, first name, email address (via Google OAuth)</li>
          <li><strong>Message content</strong>: text, photos, videos you choose to save</li>
          <li><strong>Recipient data</strong>: first name and email address of the designated recipient</li>
          <li><strong>Payment data</strong>: processed exclusively by Stripe; we do not store any banking data</li>
          <li><strong>Technical data</strong>: IP address, connection date and time, browsing data</li>
          <li><strong>Proof of consent</strong>: timestamp and IP address when accepting conditions</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">3. Purposes of Processing</h2>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Service execution</strong>: storage and deferred delivery of your message to the recipient on the scheduled date</li>
          <li><strong>Account management</strong>: authentication, message management and personal space</li>
          <li><strong>Payment</strong>: secure payment processing via Stripe</li>
          <li><strong>Service improvement</strong>: anonymized usage statistics (with your consent)</li>
          <li><strong>Legal obligations</strong>: retention of transaction data in accordance with the law</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">4. Legal Basis</h2>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Contract execution</strong> (Art. 6.1.b GDPR): for providing the deferred messaging service</li>
          <li><strong>Consent</strong> (Art. 6.1.a GDPR): for analytical cookies and trackers</li>
          <li><strong>Legitimate interest</strong> (Art. 6.1.f GDPR): for processing recipient data, strictly limited to message delivery</li>
          <li><strong>Legal obligation</strong> (Art. 6.1.c GDPR): for retaining billing data</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">5. Recipient Data</h2>
        <p className="text-[#78716C] leading-relaxed">
          Recipient information (first name and email) is collected solely for delivering the scheduled message.
          It is <strong>never used for marketing purposes</strong> nor shared with third parties.
          The recipient is informed of data processing when receiving the message and may exercise their rights
          by contacting <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a>.
        </p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">6. Data Retention</h2>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Messages and files</strong>: kept until delivery date, then automatically deleted after 3 years</li>
          <li><strong>Account data</strong>: kept during service use, then 3 years after last activity</li>
          <li><strong>Payment data</strong>: kept 10 years in accordance with accounting obligations</li>
          <li><strong>Technical logs</strong>: kept maximum 1 year</li>
          <li><strong>Consent records</strong>: kept 5 years</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">7. Your Rights</h2>
        <p className="text-[#78716C] leading-relaxed mb-3">Under GDPR, you have the following rights:</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Right of access</strong>: obtain a copy of your personal data</li>
          <li><strong>Right to rectification</strong>: correct inaccurate data</li>
          <li><strong>Right to erasure</strong>: request deletion of your data and account</li>
          <li><strong>Right to portability</strong>: receive your data in a structured format</li>
          <li><strong>Right to object</strong>: object to the processing of your data</li>
          <li><strong>Right to restriction</strong>: restrict the processing of your data</li>
        </ul>
        <p className="text-[#78716C] leading-relaxed mt-3">
          To exercise your rights, contact us at <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a>.
          You can also download or delete your data directly from your personal space.
        </p>
        <p className="text-[#78716C] leading-relaxed mt-3">
          For complaints, you may contact the <strong>CNIL</strong> (French Data Protection Authority): <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#E8175D] hover:underline">www.cnil.fr</a>.
        </p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">8. Hosting and Security</h2>
        <p className="text-[#78716C] leading-relaxed">Your data is hosted on secure servers located in the European Union. We implement appropriate technical and organizational measures to protect your data:</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2 mt-3">
          <li>Encrypted communications (HTTPS/TLS)</li>
          <li>Secure authentication via Google OAuth 2.0</li>
          <li>File access restricted by identity verification</li>
          <li>Secure payments via Stripe (PCI-DSS certified)</li>
          <li>Regular backups</li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">9. Cookies</h2>
        <p className="text-[#78716C] leading-relaxed mb-3">BIZOU.me uses the following cookies:</p>
        <ul className="list-disc pl-6 text-[#78716C] space-y-2">
          <li><strong>Essential cookies</strong> (authentication session): required for service operation, exempt from consent</li>
          <li><strong>Analytics cookies</strong> (Google Analytics, Microsoft Clarity): set only with your explicit consent, for measuring site traffic and analyzing user journeys (heatmaps, anonymized session recordings)</li>
          <li><strong>Marketing cookies</strong> (Meta Pixel): set only with your explicit consent</li>
        </ul>
        <p className="text-[#78716C] leading-relaxed mt-3">You can change your preferences at any time by clicking "Manage cookies" at the bottom of the page.</p>
      </section>
      <section>
        <h2 className="text-xl font-semibold mb-3">10. Changes</h2>
        <p className="text-[#78716C] leading-relaxed">
          We reserve the right to modify this privacy policy. In case of substantial changes,
          you will be notified by email or through a notification on the service. The last updated date is shown at the top of this page.
        </p>
      </section>
      <section className="bg-[#FDF0F4] rounded-2xl p-6 mt-10">
        <h2 className="text-xl font-semibold mb-3">Contact</h2>
        <p className="text-[#78716C] leading-relaxed">
          For any questions regarding the protection of your data:<br />
          <strong>My-Memoire SASU</strong><br />
          Email: <a href="mailto:privacy@my-memoire.com" className="text-[#E8175D] hover:underline">privacy@my-memoire.com</a><br />
          Website: <a href="http://www.my-memoire.com" target="_blank" rel="noopener noreferrer" className="text-[#E8175D] hover:underline">www.my-memoire.com</a>
        </p>
      </section>
    </div>
  );
}
